import { createContext, useContext, useMemo, type ReactNode } from "react";
import { applyTheme } from "@plataforma/design-system";
import type { FeatureKey, PublicAppConfig } from "@plataforma/shared";
import { createFormatters, type Formatters } from "../lib/format";

interface AppConfigValue {
  config: PublicAppConfig;
  format: Formatters;
  has: (feature: FeatureKey) => boolean;
}

const AppConfigContext = createContext<AppConfigValue | null>(null);

/** Aplica marca e tema da aplicação ao documento. É o único ponto que traduz configuração em aparência. */
export function applyBranding(config: PublicAppConfig, target: Document = document): void {
  applyTheme(config.theme, target.documentElement);
  target.title = `${config.name}${config.branding.tagline ? ` | ${config.branding.tagline}` : ""}`;
  target.documentElement.lang = config.settings.locale;
  if (config.branding.faviconUrl) {
    const icon = target.querySelector<HTMLLinkElement>("link[rel='icon']") ?? target.head.appendChild(Object.assign(target.createElement("link"), { rel: "icon" }));
    icon.href = config.branding.faviconUrl;
  }
}

export function AppConfigProvider({ config, children }: { config: PublicAppConfig; children: ReactNode }) {
  const value = useMemo<AppConfigValue>(
    () => ({ config, format: createFormatters(config.settings.locale, config.settings.currency), has: (feature) => config.features.includes(feature) }),
    [config],
  );
  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
}

export function useAppConfig(): AppConfigValue {
  const value = useContext(AppConfigContext);
  if (!value) throw new Error("useAppConfig precisa do AppConfigProvider");
  return value;
}
